import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-away chrome (bottom nav pill, chat button): hidden while the user
 * scrolls down the page, back the moment they scroll up. A direction flip
 * needs a few px of travel before it counts, so scroll jitter (iOS momentum,
 * rubber-banding) doesn't strobe the chrome. Near the top of the page the
 * chrome is always shown.
 *
 * Listens to window scroll — app pages scroll the document, not an inner
 * container (see ResponsiveLayout).
 */
const HIDE_AFTER = 24; // px of downward travel before hiding
const SHOW_AFTER = 12; // px of upward travel before revealing
const ALWAYS_SHOW_Y = 80; // never hidden this close to the top

export function useHideOnScroll(): boolean {
    const [hidden, setHidden] = useState(false);
    const lastY = useRef(0);
    const travel = useRef(0); // accumulated same-direction scroll distance

    useEffect(() => {
        lastY.current = window.scrollY;

        const onScroll = () => {
            const y = window.scrollY;
            const delta = y - lastY.current;
            lastY.current = y;
            if (delta === 0) return;

            if (y <= ALWAYS_SHOW_Y) {
                travel.current = 0;
                setHidden(false);
                return;
            }

            // Direction changed — start counting travel from zero.
            if (delta > 0 !== travel.current > 0) travel.current = 0;
            travel.current += delta;

            if (travel.current > HIDE_AFTER) setHidden(true);
            else if (travel.current < -SHOW_AFTER) setHidden(false);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return hidden;
}
